import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import StandardHeader from '../layout/StandardHeader';
import api from '../../api';

/* ── CSS injection ── */
if (!document.head.querySelector('[data-admin-stats-styles]')) {
    const el = document.createElement('style');
    el.dataset.adminStatsStyles = '1';
    el.textContent = `
        @keyframes spin { to { transform: rotate(360deg); } }
        .arow td { transition: background 0.15s ease; }
        .arow:hover td { background: rgba(255,255,255,0.035) !important; }
    `;
    document.head.appendChild(el);
}

const AdminPlayerStatsPage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();

    const [saisons, setSaisons] = useState([]);
    const [selectedSaison, setSelectedSaison] = useState('all');
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSaisons, setLoadingSaisons] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'displayName', dir: 'asc' });
    const [search, setSearch] = useState('');
    const [showZero, setShowZero] = useState(false);

    // Load seasons once
    useEffect(() => {
        if (!serverId) return;
        api.get(`/servers/${serverId}/saisons`)
            .then(res => setSaisons(Array.isArray(res.data) ? res.data : []))
            .catch(() => setSaisons([]))
            .finally(() => setLoadingSaisons(false));
    }, [serverId]);

    // Load player stats on serverId or season change
    const fetchStats = useCallback(async () => {
        if (!serverId) return;
        setLoading(true);
        try {
            const params = selectedSaison !== 'all' ? { saisonId: selectedSaison } : {};
            const res = await api.get(`/stats/${serverId}/admin/players`, { params });
            setPlayers(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Erreur chargement stats joueurs:', err);
            setPlayers([]);
        } finally {
            setLoading(false);
        }
    }, [serverId, selectedSaison]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Sorting
    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: key === 'displayName' ? 'asc' : 'desc' }
        );
    };

    const sortIcon = (key) => {
        if (sortConfig.key !== key) return ' ↕';
        return sortConfig.dir === 'asc' ? ' ↑' : ' ↓';
    };

    const filtered = players
        .filter(p => p.displayName.toLowerCase().includes(search.toLowerCase()))
        .filter(p => showZero || p.attacks > 0 || p.defenses > 0)
        .sort((a, b) => {
            const { key, dir } = sortConfig;
            const valA = typeof a[key] === 'string' ? a[key].toLowerCase() : a[key];
            const valB = typeof b[key] === 'string' ? b[key].toLowerCase() : b[key];
            if (valA < valB) return dir === 'asc' ? -1 : 1;
            if (valA > valB) return dir === 'asc' ? 1 : -1;
            return 0;
        });

    return (
        <div style={styles.page}>
            <StandardHeader
                title={t('adminStats.title')}
                subtitle={t('adminStats.subtitle')}
            />

            <div style={styles.innerBox}>

                {/* Filters bar */}
                <div style={styles.filtersBar}>
                    <div style={styles.leftFilters}>
                        {/* Season selector */}
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>{t('adminStats.season')}</label>
                            {loadingSaisons ? (
                                <span style={styles.loadingText}>{t('common.loading')}</span>
                            ) : (
                                <select
                                    style={styles.select}
                                    value={selectedSaison}
                                    onChange={e => setSelectedSaison(e.target.value)}
                                >
                                    <option value="all">{t('adminStats.allSeasons')}</option>
                                    {saisons.map(s => (
                                        <option key={s._id} value={s._id}>{s.title}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Search */}
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>{t('common.search')}</label>
                            <input
                                style={styles.searchInput}
                                type="text"
                                placeholder={t('adminStats.searchPlaceholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        {/* Show zero toggle */}
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>{t('adminStats.showZeroLabel')}</label>
                            <label style={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={showZero}
                                    onChange={e => setShowZero(e.target.checked)}
                                    style={styles.toggleCheckbox}
                                />
                                {t('adminStats.showZeroPlayers')}
                            </label>
                        </div>
                    </div>

                    {/* Refresh */}
                    <button style={styles.refreshBtn} onClick={fetchStats} disabled={loading}>
                        🔄 {t('common.refresh')}
                    </button>
                </div>

                {/* Table */}
                <div style={styles.tableWrapper}>
                    {loading ? (
                        <div style={styles.loadingState}>
                            <div style={styles.spinner}></div>
                            <p style={{ color: '#888', margin: 0 }}>{t('common.loading')}</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
                            <p style={{ color: '#888', margin: 0, fontSize: 14 }}>{t('adminStats.noPlayers')}</p>
                        </div>
                    ) : (
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.theadRow}>
                                    <th style={{ ...styles.th, width: 52, textAlign: 'center' }}>#</th>
                                    <th
                                        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('displayName')}
                                    >
                                        {t('adminStats.colName')}{sortIcon('displayName')}
                                    </th>
                                    <th
                                        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}
                                        onClick={() => handleSort('attacks')}
                                    >
                                        <span style={{ color: '#e74c3c' }}>✕</span> {t('adminStats.colAttacks')}{sortIcon('attacks')}
                                    </th>
                                    <th
                                        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}
                                        onClick={() => handleSort('defenses')}
                                    >
                                        <span style={{ color: '#3498db' }}>●</span> {t('adminStats.colDefenses')}{sortIcon('defenses')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p, i) => (
                                    <tr
                                        key={p.playerId}
                                        className="arow"
                                        style={{ backgroundColor: i % 2 === 0 ? '#1a1a1a' : '#1f1f1f' }}
                                    >
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={styles.rowNum}>{i + 1}</span>
                                        </td>
                                        <td style={styles.td}>{p.displayName}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={p.attacks > 0 ? styles.badgeAttack : styles.badgeZero}>
                                                {p.attacks}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={p.defenses > 0 ? styles.badgeDefense : styles.badgeZero}>
                                                {p.defenses}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && filtered.length > 0 && (
                    <p style={styles.summary}>
                        {filtered.length} {t('adminStats.playersCount')}
                    </p>
                )}

            </div>
        </div>
    );
};

const styles = {
    page: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#121212',
        color: '#f8f1e4',
    },
    innerBox: {
        backgroundColor: '#1a1a1a',
        padding: '25px',
        borderRadius: '12px',
        color: '#f8f1e4',
        boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.4)',
        width: '95%',
        maxWidth: '1400px',
        border: '1px solid #333',
        margin: '25px auto 40px auto',
    },
    filtersBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: '20px',
        flexWrap: 'wrap',
        marginBottom: '25px',
        padding: '15px 20px',
        backgroundColor: '#282828',
        borderRadius: '8px',
    },
    leftFilters: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '20px',
        flexWrap: 'wrap',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    filterLabel: {
        fontSize: '11px',
        color: '#888',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    select: {
        backgroundColor: '#2a2a2a',
        color: '#f8f1e4',
        border: '2px solid #444',
        borderRadius: '6px',
        padding: '10px 15px',
        fontSize: '14px',
        cursor: 'pointer',
        minWidth: '200px',
    },
    searchInput: {
        backgroundColor: '#2a2a2a',
        color: '#f8f1e4',
        border: '2px solid #444',
        borderRadius: '6px',
        padding: '10px 15px',
        fontSize: '14px',
        minWidth: '200px',
        outline: 'none',
    },
    refreshBtn: {
        padding: '12px 16px',
        fontSize: '14px',
        borderRadius: '6px',
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        alignSelf: 'flex-end',
    },
    tableWrapper: {
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #2a2a2a',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: '#1a1a1a',
    },
    theadRow: {
        backgroundColor: '#282828',
    },
    th: {
        padding: '12px 16px',
        color: '#888',
        fontWeight: '700',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        borderBottom: '2px solid #333',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    td: {
        padding: '13px 16px',
        fontSize: '14px',
        borderBottom: '1px solid #242424',
        color: '#ddd',
        verticalAlign: 'middle',
    },
    rowNum: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: '50%',
        fontSize: 12,
        fontWeight: 700,
        backgroundColor: 'rgba(212,175,55,0.12)',
        color: '#d4af37',
        border: '1px solid rgba(212,175,55,0.3)',
    },
    badgeAttack: {
        display: 'inline-block',
        backgroundColor: 'rgba(231,76,60,0.12)',
        color: '#e74c3c',
        border: '1px solid rgba(231,76,60,0.3)',
        borderRadius: '20px',
        padding: '3px 14px',
        fontWeight: '700',
        fontSize: '13px',
        minWidth: '36px',
        textAlign: 'center',
    },
    badgeDefense: {
        display: 'inline-block',
        backgroundColor: 'rgba(52,152,219,0.12)',
        color: '#3498db',
        border: '1px solid rgba(52,152,219,0.3)',
        borderRadius: '20px',
        padding: '3px 14px',
        fontWeight: '700',
        fontSize: '13px',
        minWidth: '36px',
        textAlign: 'center',
    },
    badgeZero: {
        display: 'inline-block',
        backgroundColor: 'transparent',
        color: '#555',
        borderRadius: '20px',
        padding: '3px 14px',
        fontWeight: '600',
        fontSize: '13px',
        minWidth: '36px',
        textAlign: 'center',
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        gap: '16px',
        backgroundColor: '#1a1a1a',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #444',
        borderTopColor: '#3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        backgroundColor: '#1a1a1a',
        border: '2px dashed #2a2a2a',
        borderRadius: '8px',
    },
    loadingText: {
        color: '#888',
        fontSize: '14px',
    },
    toggleLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#f8f1e4',
        cursor: 'pointer',
        userSelect: 'none',
        padding: '10px 0',
    },
    toggleCheckbox: {
        width: '16px',
        height: '16px',
        accentColor: '#3498db',
        cursor: 'pointer',
    },
    summary: {
        marginTop: '14px',
        fontSize: '13px',
        color: '#666',
        textAlign: 'right',
    },
};

export default AdminPlayerStatsPage;
